import { Link } from "react-router-dom";
import { Sparkles, Leaf, ShieldCheck, Droplet } from "lucide-react";
import { ESSENTIAL_OILS } from "@/lib/company";

function OilCard({ oil }) {
  return (
    <div
      data-testid={`oil-card-${oil.slug}`}
      className="product-card bg-white rounded-lg border border-neutral-200 overflow-hidden flex flex-col"
    >
      <div className="aspect-[4/3] bg-[#F1EDDE] flex items-center justify-center overflow-hidden">
        {oil.image ? (
          <img src={oil.image} alt={oil.name} className="w-full h-full object-cover" />
        ) : (
          <div className="text-center px-8">
            <Droplet className="w-14 h-14 text-brand-green mx-auto opacity-70" />
            <div className="mt-3 text-xs font-bold tracking-[0.3em] uppercase text-brand-gold">
              Photography Coming Soon
            </div>
          </div>
        )}
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-brand-charcoal text-xl leading-snug">
              {oil.name}
            </h3>
            <p className="mt-1 text-xs italic text-brand-charcoal/60">{oil.botanical}</p>
          </div>
          {oil.premium && (
            <span className="text-[9px] tracking-widest uppercase font-bold text-brand-gold border border-brand-gold/60 rounded-full px-2 py-0.5 shrink-0">
              Premium
            </span>
          )}
        </div>

        <div className="mt-4 space-y-2 text-xs">
          <Row label="Origin" value={oil.origin} />
          <Row label="Certification" value={oil.certification} highlight={oil.certification.includes("Organic")} />
          <Row label="Method" value={oil.method} />
          <Row label="Key Marker" value={oil.keyMarkers} />
          <Row label="Aroma" value={oil.profile} italic />
        </div>

        <div className="mt-4 pt-4 border-t border-neutral-200 text-xs text-brand-charcoal/75 leading-relaxed">
          <span className="font-bold text-brand-green uppercase tracking-wider text-[10px]">Best Use · </span>
          {oil.uses}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2 text-[10px]">
          <MiniStat label="Packaging" value={oil.packaging.split(" · ")[0]} />
          <MiniStat label="MOQ" value={oil.moq} />
          <MiniStat label="Lead Time" value={oil.lead} />
        </div>

        <Link
          to="/contact"
          data-testid={`inquire-${oil.slug}`}
          className="mt-5 btn-outline-green text-sm self-start"
          style={{ padding: "0.55rem 1.1rem" }}
        >
          Request Quotation
        </Link>
      </div>
    </div>
  );
}

const Row = ({ label, value, highlight, italic }) => (
  <div className="flex gap-2">
    <span className="text-[10px] font-bold tracking-widest uppercase text-brand-gold w-[85px] shrink-0 pt-0.5">
      {label}
    </span>
    <span className={`flex-1 text-brand-charcoal/85 ${italic ? "italic" : ""} ${highlight ? "font-semibold text-brand-green" : ""}`}>
      {value}
    </span>
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="bg-[#F7F5EF] rounded p-2 text-center">
    <div className="text-[8px] font-bold uppercase tracking-widest text-brand-gold">{label}</div>
    <div className="mt-1 text-[10px] font-semibold text-brand-charcoal">{value}</div>
  </div>
);

export default function EssentialOils() {
  return (
    <div data-testid="essential-oils-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden bg-[#1B2A21]">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(ellipse at 30% 50%, rgba(201,162,39,0.35) 0%, transparent 55%), radial-gradient(ellipse at 80% 60%, rgba(27,94,32,0.5) 0%, transparent 60%)",
          }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-5">
            Category F · New
          </span>
          <h1 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            Steam-Distilled &amp; Solvent-Extracted<br />
            <span className="text-brand-gold italic font-light">Essential Oils &amp; Absolutes.</span>
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-3xl leading-relaxed">
            Authentic Indian aromatic oils — steam-distilled from single-origin botanicals in
            Kerala, Uttar Pradesh, Karnataka and the Himalayan foothills. Food-grade, cosmetic-
            grade, and organic-certified variants for perfumery, flavour, aromatherapy and
            pharmaceutical applications.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="oils-hero-cta" className="btn-gold">
              <Sparkles className="w-4 h-4" />
              Request Sample Kit
            </Link>
            <Link to="#oils-catalog" data-testid="oils-hero-browse" className="btn-outline-white">
              Browse Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="py-10 bg-brand-cream border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Leaf, title: "100% Pure", note: "No dilution · no synthetic isolates" },
            { icon: ShieldCheck, title: "GC-MS Assayed", note: "Every batch fingerprinted" },
            { icon: Droplet, title: "10 Aromatic Oils", note: "Perfumery · flavour · therapy" },
            { icon: Sparkles, title: "Organic Certified", note: "ACO · USDA options available" },
          ].map((v) => (
            <div key={v.title} className="flex items-start gap-3">
              <v.icon className="w-6 h-6 text-brand-gold shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-brand-charcoal text-sm">{v.title}</div>
                <div className="text-xs text-brand-charcoal/70 mt-0.5">{v.note}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Catalogue */}
      <section id="oils-catalog" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10 max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">
              Complete Portfolio
            </span>
            <h2 className="mt-3 font-display font-bold text-brand-charcoal text-3xl md:text-4xl gold-underline">
              Ten Aromatic Oils.<br />
              <span className="text-brand-green">One Uncompromising Standard.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ESSENTIAL_OILS.map((oil) => (
              <OilCard key={oil.slug} oil={oil} />
            ))}
          </div>
        </div>
      </section>

      {/* Quality note */}
      <section className="py-16 bg-brand-green text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 className="font-display font-bold text-2xl md:text-3xl">Documentation Included with Every Shipment</h3>
          <p className="mt-5 text-white/85 leading-relaxed">
            Batch-specific GC-MS chromatogram · Certificate of Analysis · Certificate of Origin ·
            MSDS · Allergen declaration · Organic scope certificate (where applicable) ·
            Pesticide-residue &amp; heavy-metals lab report.
          </p>
          <Link to="/contact" data-testid="oils-cta-quote" className="mt-8 btn-gold inline-flex">
            Request a Quote
          </Link>
        </div>
      </section>
    </div>
  );
}
